import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSchools } from './useSchools';
import { useSchoolIdForAdmin } from './useSchoolIdForAdmin';
import { getTenantSchema, setTenantSchema } from 'utils/storage';

export const useTenantHeader = () => {
  const { user, isLoading: authLoading, isSchoolAdmin } = useAuth();
  const { useSchoolById } = useSchools(false, { enableQuery: false, disableSchoolAdmins: true });
  const [tenantLoading, setTenantLoading] = useState(true);

  // Memoize the condition to prevent unnecessary recalculations
  const shouldFetchAdminSchoolId = useMemo(() => {
    return !!user && isSchoolAdmin() && !user.schoolId;
  }, [user, user?.schoolId]); // Only depend on user and schoolId, not the function reference

  // Memoize adminId to prevent unnecessary query key changes
  const adminId = useMemo(() => {
    return shouldFetchAdminSchoolId ? user?.id : undefined;
  }, [shouldFetchAdminSchoolId, user?.id]);

  const adminSchoolIdQuery = useSchoolIdForAdmin(adminId);

  // Determine effective schoolId to use - memoize to prevent unnecessary recalculations
  const effectiveSchoolId = useMemo(() => {
    return user?.schoolId ?? adminSchoolIdQuery.data ?? undefined;
  }, [user?.schoolId, adminSchoolIdQuery.data]);

  // Fetch the school by schoolId from user
  const { data: school, isLoading: schoolLoading } = useSchoolById(effectiveSchoolId);

  useEffect(() => {
    // Try to apply any saved tenant schema immediately to avoid blocking
    const savedSchema = getTenantSchema();
    if (savedSchema && !axios.defaults.headers.common['X-Tenant-ID']) {
      axios.defaults.headers.common['X-Tenant-ID'] = savedSchema;
    }
  }, []);

  useEffect(() => {
    // Wait until auth is done, and admin schoolId lookup if needed is done,
    // and the school fetch (when we have an id) is done
    const adminLookupLoading = shouldFetchAdminSchoolId && adminSchoolIdQuery.isLoading;
    const mustWaitForSchoolFetch = !!effectiveSchoolId && schoolLoading;
    if (authLoading || adminLookupLoading || mustWaitForSchoolFetch) return;

    // Check if tenant schema already exists in sessionStorage - preserve it strictly
    const existingTenantSchema = getTenantSchema();
    
    // If we have a school with tenant info, set the tenant header
    // BUT only if no tenant schema exists in sessionStorage (preserve existing one)
    const tenantSchema = school?.tenant?.schemaName;
    if (tenantSchema) {
      // Only set tenant schema if it doesn't already exist in sessionStorage
      if (!existingTenantSchema) {
        axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
        setTenantSchema(tenantSchema);
      } else {
        // Preserve existing tenant schema - just ensure header is set
        axios.defaults.headers.common['X-Tenant-ID'] = existingTenantSchema;
      }
    } else if (existingTenantSchema) {
      // If no school tenant schema but we have existing one, ensure header is set
      axios.defaults.headers.common['X-Tenant-ID'] = existingTenantSchema;
    }

    // In all cases, loading is over by now (either we set header or we proceed without it)
    setTenantLoading(false);
  }, [
    authLoading,
    schoolLoading,
    school?.tenant?.schemaName, // Only depend on the specific property we use
    shouldFetchAdminSchoolId,
    adminSchoolIdQuery.isLoading,
    effectiveSchoolId,
  ]);

  return { tenantLoading };
};
